import { telemetry } from "telemetry";
import { log } from "utils/log";

import { generateCodeChallenge } from "./utils";

const l = log.utils.auth;

/**
 * NOTE: This authentication module uses Novorender OAuth flow.
 *
 * For Procore backend integration (backendProvider: 'procore'):
 * - Authentication is handled by Procore's OAuth system (external to this library)
 * - The useInitLibAuth hook skips calling this function when backendProvider is 'procore'
 * - The consuming application (e.g., document-viewer-ui-service) will already be authenticated with Procore
 * - No additional authentication is needed in the viewer
 *
 * This function is only used when backendProvider is 'novorender' (default).
 */

export const NOVOLIB_AUTH_CODE_KEY = "novolib_auth_code";
export const NOVOLIB_AUTH_CODE_VERIFIER_KEY = "novolib_auth_code_verifier";
export const NOVOLIB_REFRESH_TOKEN_KEY = "novolib_refresh_token";
export const NOVOLIB_REDIRECTED_KEY = "novolib_auth_redirected";

export const NOVORENDER_CLIENT_ID = "019777b6-52b2-7b61-8359-3e00f79b551e";
const NOVORENDER_AUTH_URL = "https://auth.novorender.com";

type RefreshToken = {
    token: string;
    expires: number;
};

type AuthResponse = {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    refresh_token_expires_in: number;
    token_type: string;
};

/**
 * Attempts to authenticate the user by first using an auth code from session storage,
 * and if unavailable, falls back to using a refresh token from local storage.
 * If neither method yields a valid authentication response, logs a message and redirects to the authentication flow.
 *
 * @returns A promise that resolves to an `AuthResponse` if authentication is successful, or `undefined` otherwise.
 */
export async function authenticateNovolib(): Promise<AuthResponse | undefined> {
    return _authenticateNovolib(window);
}

export async function _authenticateNovolib(win: Window): Promise<AuthResponse | undefined> {
    const authResp = (await tryUseAuthCodeFromSessionStorage(win)) ?? (await tryUseRefreshTokenFromLocalStorage(win));

    if (!authResp) {
        l.debug("No valid auth response found");
        await redirectToAuth(win);
    }

    return authResp;
}

async function tryUseRefreshTokenFromLocalStorage(win: Window) {
    try {
        const refreshTokenStr = localStorage.getItem(NOVOLIB_REFRESH_TOKEN_KEY);
        if (!refreshTokenStr) {
            l.debug("No refresh token found in local storage");
            return undefined;
        }

        const { token, expires } = JSON.parse(refreshTokenStr) as RefreshToken;

        // Token expired
        if (Date.now() >= expires) {
            l.debug("Refresh token found but expired");
            localStorage.removeItem(NOVOLIB_REFRESH_TOKEN_KEY);
            return undefined;
        }

        l.debug("Using refresh token to get new access token");
        // Use refresh token to get new access token
        const tokenUrl = `${NOVORENDER_AUTH_URL}/token`;
        const res = await win.fetch(tokenUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            mode: "cors",
            body: new URLSearchParams({
                grant_type: "refresh_token",
                client_id: NOVORENDER_CLIENT_ID,
                refresh_token: token,
            }),
        });

        if (!res.ok) {
            l.debug("Failed to refresh access token");
            localStorage.removeItem(NOVOLIB_REFRESH_TOKEN_KEY);
            throw res.statusText;
        }
        const data = (await res.json()) as AuthResponse | undefined;

        if (!data?.access_token) {
            l.debug("No access token found in refresh token response");
            localStorage.removeItem(NOVOLIB_REFRESH_TOKEN_KEY);
            return undefined;
        }

        saveRefreshTokenToLocalStorage(data);

        return data;
    } catch (e) {
        l.debug("Error refreshing access token");
        telemetry.trackCaughtException({
            source: "auth.authenticateNovolib.tryUseRefreshToken",
            category: "auth",
            error: e,
        });
        localStorage.removeItem(NOVOLIB_REFRESH_TOKEN_KEY);
    }

    return undefined;
}

async function tryUseAuthCodeFromSessionStorage(win: Window) {
    const code = sessionStorage.getItem(NOVOLIB_AUTH_CODE_KEY);

    if (!code) {
        l.debug("No auth code found in session storage");
        return undefined;
    }

    try {
        const tokenUrl = `${NOVORENDER_AUTH_URL}/token`;

        l.debug("Exchanging auth code for tokens");
        const res = await win.fetch(tokenUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            mode: "cors",
            body: new URLSearchParams({
                grant_type: "authorization_code",
                client_id: NOVORENDER_CLIENT_ID,
                code,
                redirect_uri: redirectUri(win),
                code_verifier: sessionStorage.getItem(NOVOLIB_AUTH_CODE_VERIFIER_KEY)!,
            }),
        });

        if (!res.ok) {
            l.debug("Failed to exchange auth code for tokens");
            throw res.statusText;
        }
        const data = (await res.json()) as AuthResponse | undefined;

        if (!data?.access_token) {
            l.debug("No access token found in auth code response");
            return undefined;
        }

        sessionStorage.removeItem(NOVOLIB_REDIRECTED_KEY);
        saveRefreshTokenToLocalStorage(data);

        return data;
    } catch (e) {
        l.debug("Error exchanging auth code for tokens");
        telemetry.trackCaughtException({
            source: "auth.authenticateNovolib.tryUseAuthCode",
            category: "auth",
            error: e,
        });
    } finally {
        l.debug("Cleaning up session storage");
        sessionStorage.removeItem(NOVOLIB_AUTH_CODE_KEY);
        sessionStorage.removeItem(NOVOLIB_AUTH_CODE_VERIFIER_KEY);
    }

    return undefined;
}

function saveRefreshTokenToLocalStorage(res: AuthResponse) {
    if (res.refresh_token && res.refresh_token_expires_in) {
        l.debug("Saving refresh token to local storage");
        localStorage.setItem(
            NOVOLIB_REFRESH_TOKEN_KEY,
            JSON.stringify({
                token: res.refresh_token,
                expires: Date.now() + res.refresh_token_expires_in * 1000,
            } as RefreshToken),
        );
    } else {
        l.debug("No refresh token found");
    }
}

function redirectUri(win: Window) {
    return new URL("webclients/host/oauth/callback", win.location.origin).href;
}

function getRedirectPath(win: Window) {
    // Current path + query (e.g. "/foo?bar=baz")
    const { pathname, search } = win.location;
    const fullPath = pathname + search;

    // Base path:
    // - From <base href="..."> when present
    // - Falls back to the document URL when absent
    const basePath = new URL(win.document.baseURI).pathname.replace(/\/$/, "");

    // Strip the base path only when it is a real mount prefix
    if (basePath !== "/" && pathname.startsWith(basePath + "/")) {
        return fullPath.slice(basePath.length);
    }

    // No <base> tag or no mount prefix — return unchanged
    return fullPath;
}

export async function redirectToAuth(win: Window) {
    if (sessionStorage.getItem(NOVOLIB_REDIRECTED_KEY) === "true") {
        l.debug("Already redirected to auth once, not redirecting again to avoid loop");
        return;
    }

    const stateId = win.crypto.randomUUID();
    const redirectPath = getRedirectPath(win);
    const stateStr = JSON.stringify({
        redirectPath,
        codeSessionStorageKey: NOVOLIB_AUTH_CODE_KEY,
    });
    sessionStorage.setItem(stateId, stateStr);

    const [verifier, challenge] = await generateCodeChallenge();
    sessionStorage.setItem(NOVOLIB_AUTH_CODE_VERIFIER_KEY, verifier);

    const loginUrl = new URL(`${NOVORENDER_AUTH_URL}/auth`);
    loginUrl.searchParams.set("response_type", "code");
    loginUrl.searchParams.set("client_id", NOVORENDER_CLIENT_ID);

    loginUrl.searchParams.set("redirect_uri", redirectUri(win));
    loginUrl.searchParams.set("state", stateId);
    loginUrl.searchParams.set("code_challenge", challenge);
    loginUrl.searchParams.set("code_challenge_method", "S256");
    loginUrl.searchParams.set("prompt", "Procore");

    sessionStorage.setItem(NOVOLIB_REDIRECTED_KEY, "true");

    l.debug(`Redirecting to auth: ${loginUrl}`);
    win.location.assign(loginUrl);
}
